import { Link } from "react-router-dom";
import { Film, Trash2, Clock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

function StatusBadge({ status }) {
  const cfg = {
    queued: { icon: Clock, label: "Queued", color: "text-muted-foreground border-white/10" },
    extracting: { icon: Loader2, label: "Extracting", color: "text-primary border-primary/40", spin: true },
    transcribing: { icon: Loader2, label: "Transcribing", color: "text-primary border-primary/40", spin: true },
    done: { icon: CheckCircle2, label: "Done", color: "text-green-400 border-green-500/40" },
    failed: { icon: AlertCircle, label: "Failed", color: "text-destructive border-destructive/50" },
  }[status] || { icon: Clock, label: status, color: "text-muted-foreground border-white/10" };
  const Icon = cfg.icon;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] font-mono tracking-widest uppercase ${cfg.color}`}
      data-testid={`status-badge-${status}`}
    >
      <Icon className={`w-3 h-3 ${cfg.spin ? "animate-spin" : ""}`} />
      {cfg.label}
    </div>
  );
}

function formatDuration(sec) {
  if (!sec || sec <= 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

function formatBytes(b) {
  if (!b) return "—";
  const mb = b / (1024 * 1024);
  return mb > 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

export default function HistoryGrid({ videos, onDelete, empty }) {
  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this video and its transcript?")) return;
    try {
      await api.delete(`/videos/${id}`);
      toast.success("Deleted");
      onDelete?.(id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!videos || videos.length === 0) {
    return (
      <div className="card-surface p-12 text-center" data-testid="history-empty">
        <Film className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
        <div className="font-medium">No videos yet</div>
        <div className="text-sm text-muted-foreground mt-1">
          {empty || "Upload your first video above to get started."}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="history-grid">
      {videos.map((v, idx) => (
        <Link
          key={v.id}
          to={`/video/${v.id}`}
          data-testid={`history-card-${v.id}`}
          style={{ animationDelay: `${idx * 40}ms` }}
          className="fade-up group card-surface hover:border-primary/40 transition-colors duration-200 flex flex-col"
        >
          <div className="relative aspect-video bg-black/60 border-b border-white/5 overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <Film className="w-12 h-12 text-white/10 group-hover:text-primary/40 transition-colors" />
            </div>
            <div className="absolute top-3 left-3">
              <StatusBadge status={v.status} />
            </div>
            {v.duration > 0 && (
              <div className="absolute bottom-3 right-3 font-mono text-[10px] tracking-widest px-2 py-1 bg-black/70 border border-white/10">
                {formatDuration(v.duration)}
              </div>
            )}
          </div>
          <div className="p-4 flex-1 flex flex-col">
            <div className="font-medium truncate" title={v.filename}>
              {v.filename}
            </div>
            <div className="flex items-center gap-2 mt-1 text-[11px] font-mono text-muted-foreground">
              <span>{formatBytes(v.size)}</span>
              {v.language && (
                <>
                  <span>·</span>
                  <span className="uppercase">{v.language}</span>
                </>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
              <span className="text-[10px] font-mono text-muted-foreground">
                {new Date(v.created_at).toLocaleString()}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => remove(v.id, e)}
                data-testid={`delete-video-${v.id}`}
                className="h-8 w-8 p-0 hover:bg-destructive/20 hover:text-destructive"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
